import { useState, useMemo } from 'react';
import type { ImageAttribution } from '@nfa/types';

export interface RouteHeroProps {
  imageUrl?: string;
  attribution?: ImageAttribution;
  routeName: string;
}

/**
 * Hero image dimensions for CLS prevention (Phase 5.2)
 * Wide aspect ratio for hero banner
 */
const HERO_WIDTH = 1200;
const HERO_HEIGHT = 320;

/**
 * Responsive image widths for srcset
 * Matches Tailwind breakpoints: mobile, tablet, desktop
 */
const SRCSET_WIDTHS = [640, 1024, 1536];

/**
 * Convert Unsplash URL to proxy URL for edge caching.
 * Falls back to original URL if not an Unsplash image.
 */
function getProxiedImageUrl(url: string): string {
  // Only proxy Unsplash images
  if (!url.includes('images.unsplash.com')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    // Extract the image ID (e.g., "photo-1234567890")
    const imageId = parsed.pathname.replace(/^\//, '');

    // Build proxy URL
    const apiBase = import.meta.env.VITE_API_URL || 'https://api.notfuckingabout.com';
    const proxyUrl = new URL(`${apiBase}/api/images/${imageId}`);

    // Forward transformation params
    for (const [key, value] of parsed.searchParams) {
      proxyUrl.searchParams.set(key, value);
    }

    return proxyUrl.toString();
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Generate srcset for responsive images (Phase 5.2)
 * Creates multiple URLs at different widths for optimal loading
 */
function generateSrcset(baseUrl: string): string {
  return SRCSET_WIDTHS.map(width => {
    // Add width parameter to URL
    const url = new URL(baseUrl);
    url.searchParams.set('w', String(width));
    return `${url.toString()} ${width}w`;
  }).join(', ');
}

export function RouteHero({ imageUrl, attribution, routeName }: RouteHeroProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Use proxied URL for edge caching and generate responsive srcset
  const { proxiedUrl, srcset } = useMemo(() => {
    if (!imageUrl) {
      return { proxiedUrl: undefined, srcset: undefined };
    }
    const url = getProxiedImageUrl(imageUrl);
    return {
      proxiedUrl: url,
      srcset: generateSrcset(url),
    };
  }, [imageUrl]);

  const showImage = proxiedUrl && !hasError;

  return (
    <div
      data-testid="route-hero"
      className={`relative h-48 sm:h-64 lg:h-80 rounded-lg overflow-hidden ${
        !showImage ? 'bg-gradient-to-br from-orange-600 via-amber-700 to-neutral-900' : ''
      }`}
    >
      {showImage ? (
        <>
          {/* Loading skeleton */}
          {!isLoaded && (
            <div
              data-testid="route-hero-skeleton"
              className="absolute inset-0 bg-neutral-800 animate-pulse"
            />
          )}

          {/* Hero image (Phase 5.2: explicit dimensions for CLS = 0) */}
          <img
            src={proxiedUrl}
            alt={`${routeName} landscape`}
            width={HERO_WIDTH}
            height={HERO_HEIGHT}
            srcSet={srcset}
            sizes="(max-width: 640px) 640px, (max-width: 1024px) 1024px, 1536px"
            loading="lazy"
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
          />

          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Attribution */}
          {attribution && (
            <div className="absolute bottom-3 right-3 text-xs text-white/70">
              Photo by{' '}
              <a
                href={attribution.link}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white transition-colors"
              >
                {attribution.name}
              </a>
              {' on '}
              <a
                href={`https://unsplash.com/photos/${attribution.unsplash_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white transition-colors"
              >
                Unsplash
              </a>
            </div>
          )}
        </>
      ) : (
        /* Gradient fallback with route name */
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="font-mono text-2xl sm:text-3xl lg:text-4xl font-bold text-white/90 text-center px-6">
            {routeName}
          </h2>
        </div>
      )}
    </div>
  );
}
